import { Fragment } from 'react';
import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useToastStore, type ToastType } from '../../store/toastStore';

const iconMap: Record<ToastType, typeof CheckCircleIcon> = {
  success: CheckCircleIcon,
  error: ExclamationCircleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

const colorMap: Record<ToastType, { icon: string; ring: string; title: string }> = {
  success: { icon: 'text-emerald-400', ring: 'ring-emerald-500/20', title: 'text-emerald-300' },
  error: { icon: 'text-red-400', ring: 'ring-red-500/20', title: 'text-white' },
  warning: { icon: 'text-amber-400', ring: 'ring-amber-500/20', title: 'text-white' },
  info: { icon: 'text-cyan-400', ring: 'ring-cyan-500/20', title: 'text-white' },
};

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-[60] flex flex-col items-center justify-center gap-3 px-4 py-6 sm:p-6"
    >
      <div className="flex w-full flex-col items-center gap-3">
        {toasts.map((toast) => {
          const Icon = iconMap[toast.type];
          const colors = colorMap[toast.type];

          return (
            <Transition
              key={toast.id}
              show
              as={Fragment}
              enter="transform transition duration-200 ease-out"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transform transition duration-200 ease-in"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div
                className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-xl bg-slate-800 shadow-lg ring-1 ${colors.ring}`}
              >
                <div className="p-4">
                  <div className="flex items-start">
                    <Icon className={`h-5 w-5 flex-shrink-0 ${colors.icon}`} />
                    <div className="ml-3 w-0 flex-1">
                      <p className={`text-sm font-medium ${colors.title}`}>{toast.title}</p>
                      {toast.message && (
                        <p className="mt-1 text-sm text-slate-400">{toast.message}</p>
                      )}
                    </div>
                    {toast.action && (
                      <button
                        type="button"
                        className="ml-3 flex-shrink-0 text-sm font-semibold text-cyan-400 hover:text-cyan-300"
                        onClick={() => {
                          toast.action?.onClick();
                          removeToast(toast.id);
                        }}
                      >
                        {toast.action.label}
                      </button>
                    )}
                    <button
                      type="button"
                      className="ml-4 inline-flex flex-shrink-0 rounded-md text-slate-500 hover:text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-slate-800"
                      onClick={() => removeToast(toast.id)}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </Transition>
          );
        })}
      </div>
    </div>
  );
}
